// src/lib/mock/bookings.ts
// Seed data for the Phase-3 shell. Replaced by API responses in Phase 4.
// All client names are South African; production context is JHB + Cape Town.
// Pricing is per day per unit from the Glow Hire rate card.

import type { BookingJob, GearItem, StockReservation } from '@/lib/types/workflow';
import { WorkflowStatus } from '@/lib/types/workflow';
import type { AuditEntry } from '@/lib/types/audit';

// ── Gear lists ───────────────────────────────────────────────────────────────

const gearNeonHorizon: GearItem[] = [
  {
    id: 'g-1',
    name: 'ARRI Alexa Mini LF',
    quantity: 1,
    unitPrice: 8500,
    stockStatus: 'IN_STOCK',
  },
  {
    id: 'g-2',
    name: 'Zeiss Supreme Prime Set (35/50/85mm)',
    quantity: 1,
    unitPrice: 5400,
    stockStatus: 'SUBSTITUTION',
    substitutionNote:
      'Out of stock for these dates — Leica Summilux-C Set (35/50/75mm) offered as a comparable alternative.',
  },
  {
    id: 'g-3',
    name: 'Bebob V200 Gold Mount Battery',
    quantity: 6,
    unitPrice: 280,
    stockStatus: 'IN_STOCK',
  },
  {
    id: 'g-4',
    name: 'DJI RS3 Pro Gimbal',
    quantity: 1,
    unitPrice: 850,
    stockStatus: 'IN_STOCK',
  },
];

const gearRedKomodo: GearItem[] = [
  {
    id: 'g-r1',
    name: 'RED Komodo DSMC3 6K',
    quantity: 1,
    unitPrice: 6200,
    stockStatus: 'IN_STOCK',
  },
  {
    id: 'g-r2',
    name: 'Cooke S4/i Prime Set (32/50/75mm)',
    quantity: 1,
    unitPrice: 6800,
    stockStatus: 'IN_STOCK',
  },
  {
    id: 'g-r3',
    name: 'Wireless Follow Focus',
    quantity: 1,
    unitPrice: 750,
    stockStatus: 'IN_STOCK',
  },
  {
    id: 'g-r4',
    name: 'Matte Box',
    quantity: 1,
    unitPrice: 450,
    stockStatus: 'IN_STOCK',
  },
  {
    id: 'g-r5',
    name: 'Bebob V200 Gold Mount Battery',
    quantity: 4,
    unitPrice: 280,
    stockStatus: 'IN_STOCK',
  },
];

const gearBcam: GearItem[] = [
  {
    id: 'g-b1',
    name: 'ARRI Alexa Mini (B-cam)',
    quantity: 1,
    unitPrice: 5500,
    stockStatus: 'IN_STOCK',
  },
];

const gearKhanyile: GearItem[] = [
  {
    id: 'g-k1',
    name: 'Sony FX9',
    quantity: 1,
    unitPrice: 3800,
    stockStatus: 'IN_STOCK',
  },
  {
    id: 'g-k2',
    name: 'LED Panel 1×1',
    quantity: 2,
    unitPrice: 680,
    stockStatus: 'IN_STOCK',
  },
  {
    id: 'g-k3',
    name: "Director's Monitor 7\"",
    quantity: 1,
    unitPrice: 950,
    stockStatus: 'IN_STOCK',
  },
  {
    id: 'g-k4',
    name: 'DJI RS3 Pro Gimbal',
    quantity: 1,
    unitPrice: 850,
    stockStatus: 'IN_STOCK',
  },
  {
    id: 'g-k5',
    name: 'Anton Bauer Gold Mount Battery',
    quantity: 4,
    unitPrice: 260,
    stockStatus: 'IN_STOCK',
  },
];

const gearVuyo: GearItem[] = [
  {
    id: 'g-v1',
    name: 'Sony FX6',
    quantity: 1,
    unitPrice: 3800,
    stockStatus: 'IN_STOCK',
  },
  {
    id: 'g-v2',
    name: 'Leica Summilux-C Set (35/50/75mm)',
    quantity: 1,
    unitPrice: 7200,
    stockStatus: 'LOW_STOCK',
  },
  {
    id: 'g-v3',
    name: 'LED Panel 1×1',
    quantity: 2,
    unitPrice: 680,
    stockStatus: 'IN_STOCK',
  },
  {
    id: 'g-v4',
    name: 'Matte Box',
    quantity: 1,
    unitPrice: 450,
    stockStatus: 'IN_STOCK',
  },
  {
    id: 'g-v5',
    name: 'Wireless Follow Focus',
    quantity: 1,
    unitPrice: 750,
    stockStatus: 'IN_STOCK',
  },
];

// ── Queue (all PENDING_REVIEW) ────────────────────────────────────────────────

export const mockBookingQueue: BookingJob[] = [
  {
    id: '#2847-LF',
    clientName: 'Merrick & Associates',
    clientEmail: 'marcus.thorne@merrick.studio',
    receivedAt: '2026-05-13T08:42:00+02:00',
    subject: "Kit lock-in — 'Neon Horizon' shoot, Thurs",
    emailBody: [
      'Hi Team,',
      "We're looking to lock in the kit for the 'Neon Horizon' shoot next Thursday in Johannesburg (Maboneng Precinct). We need the Alexa Mini LF package and ideally the Zeiss Supreme Primes — specifically the 35, 50, and 85mm. Do you have six gold mount batteries available too?",
      'Rental period is 21–23 May, three days. Please send through a quote as soon as possible — we need to confirm with the DOP before end of day.',
      'Best,\nMarcus Thorne\nMerrick & Associates',
    ].join('\n\n'),
    status: WorkflowStatus.PENDING_REVIEW,
    isThreadReply: false,
    gear: gearNeonHorizon,
    draftSubject: "RE: Kit lock-in — 'Neon Horizon' — Quote attached",
    draftBody: [
      'Hi Marcus,',
      "Good news on most fronts. We have the Alexa Mini LF and batteries confirmed for 21–23 May. The Zeiss Supreme Primes (35/50/85mm) are unfortunately committed on those dates, but I've included the Leica Summilux-C Set (35/50/75mm) as an alternative — comparable character with a slightly warmer rendering that works beautifully on the LF sensor.",
      'Quote total for the 3-day package comes to R 49,290 VAT excl. Reservation holds for 48 hours.',
      "Let us know if the Summilux set works for the 'Neon Horizon' look and we'll lock it in.",
      'The Glow Team',
    ].join('\n\n'),
    aiConfidence: 87,
    location: 'Johannesburg (Maboneng)',
    rentalDates: '21 May – 23 May 2026 (3 days)',
    // (8500 + 5400 + 6×280 + 850) × 3 = 16 430 × 3
    quoteTotalRands: 49290,
  },
  {
    id: '#2849-RV',
    clientName: 'Sibanda Pictures',
    clientEmail: 'nomvula@sibandapictures.co.za',
    receivedAt: '2026-05-13T09:14:00+02:00',
    subject: 'Quote request — RED Komodo package, Cape Town',
    emailBody: [
      'Good morning,',
      "I'm reaching out on behalf of Sibanda Pictures regarding a RED Komodo DSMC3 package for a Cape Town shoot coming up 28–31 May. We're filming in Woodstock and need a full package: camera body, Cooke S4/i primes, and follow focus. A matte box and batteries would be ideal if available.",
      'Can you confirm availability and send through a quote? We are working to a tight schedule.',
      'Kind regards,\nNomvula Sibanda\nSibanda Pictures',
    ].join('\n\n'),
    status: WorkflowStatus.PENDING_REVIEW,
    isThreadReply: false,
    gear: gearRedKomodo,
    draftSubject: 'RE: RED Komodo package — Quote attached',
    draftBody: [
      'Hi Nomvula,',
      'Thank you for reaching out. We have the full RED Komodo DSMC3 package available for your Cape Town dates (28–31 May).',
      'Your kit as quoted includes the Komodo DSMC3, Cooke S4/i prime set (32/50/75mm), Wireless Follow Focus, Matte Box, and four Bebob V200 batteries — everything needed for a clean Woodstock run.',
      'Total comes to R 61,280 VAT excl. for the 4-day period. Reservation holds for 48 hours from the time of this quote.',
      'The Glow Team',
    ].join('\n\n'),
    aiConfidence: 92,
    location: 'Cape Town (Woodstock)',
    rentalDates: '28 May – 31 May 2026 (4 days)',
    // (6200 + 6800 + 750 + 450 + 4×280) × 4 = 15 320 × 4
    quoteTotalRands: 61280,
  },
  {
    id: '#2851-LF',
    clientName: 'Merrick & Associates',
    clientEmail: 'marcus.thorne@merrick.studio',
    receivedAt: '2026-05-13T10:05:00+02:00',
    subject: "RE: Kit lock-in — 'Neon Horizon' — Quote attached",
    emailBody: [
      'Hi team,',
      "Quick follow-up on the Neon Horizon quote. Our DOP has confirmed the Leica set is fine, but we'd like to add a second body for B-cam pickups on day 2. Same dates — 21–23 May, same location. Budget is flexible for the right kit.",
      "Can you add that to the quote and resend? We're keen to confirm today.",
      'Thanks,\nMarcus',
    ].join('\n\n'),
    status: WorkflowStatus.PENDING_REVIEW,
    isThreadReply: true,
    threadRootId: '#2847-LF',
    gear: gearBcam,
    draftSubject: "RE: Neon Horizon — B-cam added",
    draftBody: [
      'Hi Marcus,',
      "Adding the second body is no problem. I've included an ARRI Alexa Mini for B-cam pickups on day 2 — same colour science as the LF so you'll have a clean match in the grade.",
      'This adds R 16,500 to the package for the 3-day period. Updated quote to follow.',
      'The Glow Team',
    ].join('\n\n'),
    aiConfidence: 94,
    location: 'Johannesburg (Maboneng)',
    rentalDates: '21 May – 23 May 2026 (3 days)',
    // 5500 × 1 × 3 = 16 500
    quoteTotalRands: 16500,
  },
  {
    id: '#2845-PH',
    clientName: 'Khanyile Studios',
    clientEmail: 'thando@khanyilestudios.co.za',
    receivedAt: '2026-05-12T14:22:00+02:00',
    subject: 'Sony FX9 package — campaign shoot, Sandton',
    emailBody: [
      'Hi Glow,',
      'We have a two-day campaign stills/video shoot coming up in Sandton on the 19th and 20th of May. Looking for a Sony FX9 for the video component, two 1×1 LED panels, and a director\'s monitor. A gimbal would also help for the walking sequences.',
      'Please quote and confirm availability.',
      'Regards,\nThando Khanyile\nKhanyile Studios',
    ].join('\n\n'),
    status: WorkflowStatus.PENDING_REVIEW,
    isThreadReply: false,
    gear: gearKhanyile,
    draftSubject: 'RE: Sony FX9 package — Quote attached',
    draftBody: [
      'Hi Thando,',
      "Sony FX9, LED panels, director's monitor, RS3 Pro gimbal, and Anton Bauer batteries — all confirmed for the 19th and 20th. The full mobile package for a clean Sandton run.",
      'Total: R 16,000 VAT excl. for 2 days. Reservation holds for 48 hours.',
      'The Glow Team',
    ].join('\n\n'),
    aiConfidence: 91,
    location: 'Sandton, Johannesburg',
    rentalDates: '19 May – 20 May 2026 (2 days)',
    // (3800 + 2×680 + 950 + 850 + 4×260) × 2 = 8 000 × 2
    quoteTotalRands: 16000,
  },
  {
    id: '#2853-FX',
    clientName: 'Vuyo Mthembu Photography',
    clientEmail: 'vuyo@vuyomthembu.co.za',
    receivedAt: '2026-05-14T07:55:00+02:00',
    subject: 'TVC package — Sony FX6 + primes, Cape Town',
    emailBody: [
      'Hi,',
      "I'm Vuyo Mthembu — we're doing a TVC in Cape Town (Woodstock / Bree Street area) and need a cinema package for 2 days. Preferably Sony FX6 with a high-end prime set. We have some interior shots so lighting support would help — two 1×1 panels if possible.",
      'Could you quote on a package? Dates are the 22nd and 23rd of May.',
      'Thanks,\nVuyo',
    ].join('\n\n'),
    status: WorkflowStatus.PENDING_REVIEW,
    isThreadReply: false,
    gear: gearVuyo,
    draftSubject: 'RE: TVC package — Quote attached',
    draftBody: [
      'Hi Vuyo,',
      "We can accommodate your TVC shoot on the 22nd and 23rd. The FX6 is a great match for the brief — we've paired it with the Leica Summilux-C Set which is currently on low stock, so I'd recommend confirming within 24 hours to secure it.",
      "Rounding out the package: two 1×1 LED panels for your interior shots, a matte box, and wireless follow focus for the walk-and-talk sequences.",
      'Package total: R 27,120 VAT excl. for 2 days.',
      'The Glow Team',
    ].join('\n\n'),
    aiConfidence: 83,
    location: 'Cape Town (Woodstock)',
    rentalDates: '22 May – 23 May 2026 (2 days)',
    // (3800 + 7200 + 2×680 + 450 + 750) × 2 = 13 560 × 2
    quoteTotalRands: 27120,
  },
];

// ── Backward-compat aliases ───────────────────────────────────────────────────

export const MOCK_BOOKINGS: BookingJob[] = mockBookingQueue;

export const getActiveBooking = (): BookingJob => mockBookingQueue[0];

export const getProcessingBooking = (): BookingJob => mockBookingQueue[1];

export const getThreadReplyBooking = (): BookingJob =>
  mockBookingQueue.find((b) => b.isThreadReply) ?? mockBookingQueue[2];

export const getMockBookingById = (id: string): BookingJob | undefined =>
  mockBookingQueue.find((b) => b.id === id);

// ── Stock reservation mock ────────────────────────────────────────────────────

export const mockStockReservation: StockReservation = {
  id: 'rsv-001',
  bookingId: '#2847-LF',
  lines: [
    {
      gearItemId: 'g-1',
      name: 'ARRI Alexa Mini LF',
      quantity: 1,
      unitPrice: 8500,
      reservationStatus: 'HELD',
      substituteAvailable: false,
      expiresAt: '2026-05-16T09:00:00+02:00',
      rmsHoldId: 'rms-hold-0001',
    },
    {
      gearItemId: 'g-2',
      name: 'Zeiss Supreme Prime Set (35/50/85mm)',
      quantity: 1,
      unitPrice: 5400,
      reservationStatus: 'CONFLICT',
      substituteAvailable: true,
      substituteName: 'Leica Summilux-C Set (35/50/75mm)',
      substituteUnitPrice: 7200,
      expiresAt: '2026-05-16T09:00:00+02:00',
    },
    {
      gearItemId: 'g-3',
      name: 'Bebob V200 Gold Mount Battery',
      quantity: 6,
      unitPrice: 280,
      reservationStatus: 'HELD',
      substituteAvailable: false,
      expiresAt: '2026-05-16T09:00:00+02:00',
      rmsHoldId: 'rms-hold-0002',
    },
    {
      gearItemId: 'g-4',
      name: 'DJI RS3 Pro Gimbal',
      quantity: 1,
      unitPrice: 850,
      reservationStatus: 'HELD',
      substituteAvailable: false,
      expiresAt: '2026-05-16T09:00:00+02:00',
      rmsHoldId: 'rms-hold-0003',
    },
  ],
  reservedAt: '2026-05-13T09:00:00+02:00',
  expiresAt: '2026-05-16T09:00:00+02:00',
  allHeld: false,
  requiresSubstitution: true,
};

// ── Audit log ─────────────────────────────────────────────────────────────────

export const MOCK_AUDIT: AuditEntry[] = [
  {
    id: 'a-1',
    timestamp: '2026-05-13T10:21:00+02:00',
    actorEmail: 'lerato.molefe@glow.rental',
    action: 'LOGIN',
    bookingId: '—',
  },
  {
    id: 'a-2',
    timestamp: '2026-05-13T10:24:00+02:00',
    actorEmail: 'lerato.molefe@glow.rental',
    action: 'EDITED',
    bookingId: '#2847-LF',
    notes: 'Substituted Zeiss Supreme → Leica Summilux-C',
  },
  {
    id: 'a-3',
    timestamp: '2026-05-13T10:31:00+02:00',
    actorEmail: 'lerato.molefe@glow.rental',
    action: 'APPROVED',
    bookingId: '#2847-LF',
  },
  {
    id: 'a-4',
    timestamp: '2026-05-13T10:31:12+02:00',
    actorEmail: 'lerato.molefe@glow.rental',
    action: 'SENT',
    bookingId: '#2847-LF',
    notes: 'Quote PDF attached — R 49,290.00',
  },
  {
    id: 'a-5',
    timestamp: '2026-05-13T09:58:00+02:00',
    actorEmail: 'sipho.dlamini@glow.rental',
    action: 'REJECTED',
    bookingId: '#2842-DR',
    notes: 'Dates already booked on conflicting job',
  },
  {
    id: 'a-6',
    timestamp: '2026-05-13T09:12:00+02:00',
    actorEmail: 'sipho.dlamini@glow.rental',
    action: 'RESTORED',
    bookingId: '#2839-LF',
    notes: 'Restored from deleted drafts (within 30d)',
  },
  {
    id: 'a-7',
    timestamp: '2026-05-12T17:44:00+02:00',
    actorEmail: 'lerato.molefe@glow.rental',
    action: 'SENT',
    bookingId: '#2845-PH',
  },
  {
    id: 'a-8',
    timestamp: '2026-05-12T16:30:00+02:00',
    actorEmail: 'system@glow.rental',
    action: 'DELETED',
    bookingId: '#2838-DR',
    notes: 'Auto-purged at 30-day TTL',
  },
];

// ── Archive lists ─────────────────────────────────────────────────────────────

export type SentRow = {
  bookingId: string;
  clientName: string;
  sentAt: string;
  totalRands: number;
};

export type DeletedRow = {
  bookingId: string;
  clientName: string;
  deletedAt: string;
  valueRands: number;
  withinThirtyDays: boolean;
};

export const MOCK_SENT: SentRow[] = [
  {
    bookingId: '#2843-LF',
    clientName: 'Bloemfontein Film Co.',
    sentAt: '2026-05-12T13:08:00+02:00',
    totalRands: 18450,
  },
  {
    bookingId: '#2841-RV',
    clientName: 'Cape Reels',
    sentAt: '2026-05-11T16:22:00+02:00',
    totalRands: 28200,
  },
  {
    bookingId: '#2840-PH',
    clientName: 'Vuyo Mthembu Photography',
    sentAt: '2026-05-11T11:55:00+02:00',
    totalRands: 6400,
  },
  {
    bookingId: '#2837-LF',
    clientName: 'Khanyile Studios',
    sentAt: '2026-05-10T09:30:00+02:00',
    totalRands: 49290,
  },
];

export const MOCK_DELETED: DeletedRow[] = [
  {
    bookingId: '#2842-DR',
    clientName: 'Pretoria Stage Co.',
    deletedAt: '2026-05-13T09:58:00+02:00',
    valueRands: 9800,
    withinThirtyDays: true,
  },
  {
    bookingId: '#2836-LF',
    clientName: 'Ibhayi Productions',
    deletedAt: '2026-05-08T15:11:00+02:00',
    valueRands: 14600,
    withinThirtyDays: true,
  },
  {
    bookingId: '#2812-PH',
    clientName: 'Solly Sithole Films',
    deletedAt: '2026-04-09T10:02:00+02:00',
    valueRands: 4200,
    withinThirtyDays: false,
  },
];

// ── New required exports (aliases to existing data) ───────────────────────────

export const mockSentBookings: SentRow[] = MOCK_SENT;
export const mockDeletedDrafts: DeletedRow[] = MOCK_DELETED;
export const mockAuditEntries: AuditEntry[] = MOCK_AUDIT;
